import { useState } from 'react';
import { Box, Stack, Group, Badge, Avatar, AvatarGroup, TextInput, Select, Table } from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconMapPin,
  IconRepeat,
  IconCalendarEvent,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockEvents, eventTypes, CalendarEvent } from '../mockData/calendar';

const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getStatusColor = (status: CalendarEvent['status']) => {
  switch (status) {
    case 'confirmed': return 'green';
    case 'tentative': return 'yellow';
    case 'cancelled': return 'red';
    default: return 'gray';
  }
};

export default function EventsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const statuses = ['confirmed', 'tentative', 'cancelled'];

  const filteredEvents = mockEvents.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(search.toLowerCase()) ||
                          event.description?.toLowerCase().includes(search.toLowerCase()) ||
                          event.location?.toLowerCase().includes(search.toLowerCase());
    const matchesType = !typeFilter || event.type === typeFilter;
    const matchesStatus = !statusFilter || event.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Sort by start date
  const sortedEvents = [...filteredEvents].sort((a, b) => a.start.getTime() - b.start.getTime());

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>All Events</Title>
          <Text c="dimmed" size="sm">{filteredEvents.length} events</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>New Event</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search events..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Type"
            data={eventTypes.map(t => ({ value: t.value, label: t.label }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Events Table */}
      <Card>
        {sortedEvents.length === 0 ? (
          <Box ta="center" py="xl">
            <IconCalendarEvent size={48} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed" mt="md">No events found</Text>
          </Box>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Event</Table.Th>
                <Table.Th>Date & Time</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Attendees</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedEvents.map(event => {
                const typeInfo = eventTypes.find(t => t.value === event.type);
                return (
                  <Table.Tr key={event.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 4,
                            height: 40,
                            borderRadius: 4,
                            backgroundColor: `var(--mantine-color-${event.color}-filled)`,
                          }}
                        />
                        <Box>
                          <Group gap="xs">
                            <Text size="sm" fw={500}>{event.title}</Text>
                            {event.isRecurring && <IconRepeat size={12} color="var(--mantine-color-dimmed)" />}
                          </Group>
                          <Badge size="xs" color={typeInfo?.color || 'gray'}>{event.type}</Badge>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {event.allDay ? 'All day' : formatDateTime(event.start)}
                      </Text>
                      {!event.allDay && (
                        <Text size="xs" c="dimmed">to {formatDateTime(event.end)}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {event.location ? (
                        <Group gap={4}>
                          <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="sm" c="dimmed">{event.location}</Text>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {event.attendees && event.attendees.length > 0 ? (
                        <AvatarGroup>
                          {event.attendees.slice(0, 3).map(attendee => (
                            <Avatar key={attendee.id} size="sm" radius="xl" color="blue">
                              {attendee.name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                          ))}
                          {event.attendees.length > 3 && (
                            <Avatar size="sm" radius="xl" color="gray">+{event.attendees.length - 3}</Avatar>
                          )}
                        </AvatarGroup>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={getStatusColor(event.status)}>{event.status}</Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
